import '@phosphor-icons/web/regular';

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: SpeechRecognitionResultEventLike) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

interface SpeechRecognitionResultEventLike {
  resultIndex: number;
  results: { length: number; [index: number]: { isFinal: boolean; 0: { transcript: string } } };
}

interface SpeechWindow extends Window {
  SpeechRecognition?: SpeechRecognitionCtor;
  webkitSpeechRecognition?: SpeechRecognitionCtor;
}

const bars = Array.from({ length: 12 }, () => '<i></i>').join('');
let recognition: SpeechRecognitionLike | null = null;
let keepListening = false;
let activeTarget: HTMLTextAreaElement | HTMLInputElement | null = null;
let assistantEl: HTMLElement | null = null;
let activeButton: HTMLButtonElement | null = null;
let committedTargetText = '';

function recognitionCtor(): SpeechRecognitionCtor | null {
  const speechWindow = window as SpeechWindow;
  return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition ?? null;
}

function appendText(target: HTMLTextAreaElement | HTMLInputElement, text: string): void {
  const clean = text.trim().replace(/\s+/g, ' ');
  if (!clean) return;

  const current = committedTargetText || target.value.trimEnd();
  const glue = current && !/[.\n:;!?]$/.test(current) ? '. ' : current ? ' ' : '';
  target.value = `${current}${glue}${clean}`;
  emitInput(target);
}

function emitInput(target: HTMLTextAreaElement | HTMLInputElement): void {
  target.dispatchEvent(new Event('input', { bubbles: true }));
  target.dispatchEvent(new Event('change', { bubbles: true }));
}

function correctionPairs(lang: string): Array<[RegExp, string]> {
  if (!lang.toLowerCase().startsWith('es')) return [];

  return [
    [/\bcardiologico\b/gi, 'cardiológico'],
    [/\bcardiologia\b/gi, 'cardiología'],
    [/\bclinico\b/gi, 'clínico'],
    [/\bclinica\b/gi, 'clínica'],
    [/\bpsicologico\b/gi, 'psicológico'],
    [/\bpsicologia\b/gi, 'psicología'],
    [/\boftalmologico\b/gi, 'oftalmológico'],
    [/\boftalmologia\b/gi, 'oftalmología'],
    [/\bkinesiologia\b/gi, 'kinesiología'],
    [/\bneurologico\b/gi, 'neurológico'],
    [/\bneurologia\b/gi, 'neurología'],
    [/\btraumatologico\b/gi, 'traumatológico'],
    [/\btraumatologia\b/gi, 'traumatología'],
    [/\baudiometria\b/gi, 'audiometria'],
    [/\bespirometria\b/gi, 'espirometria'],
    [/\belectro cardiograma\b/gi, 'electrocardiograma'],
    [/\bmercado pago\b/gi, 'Mercado Pago'],
    [/\bwhats ?app\b/gi, 'WhatsApp'],
    [/\bdni\b/gi, 'DNI'],
    [/\bcuit\b/gi, 'CUIT'],
    [/\bart\b/gi, 'ART'],
    [/\bprofint\b/gi, 'Profint'],
    [/\bdataclinic\b/gi, 'DataClinic'],
    [/\bdictador\b/gi, 'dictado'],
    [/\bde tareds\b/gi, 'de tareas'],
    [/\btareds\b/gi, 'tareas'],
    [/\bfuncionandoh\b/gi, 'funcionando'],
    [/\binterpretador\b/gi, 'intérprete'],
    [/\bademas\b/gi, 'además'],
    [/\bqueiro\b/gi, 'quiero'],
    [/\bpor que\b/gi, 'por qué'],
    [/\ba las (\d{1,2}) y (\d{2})\b/gi, 'a las $1:$2'],
    [/\s+([,.;:!?])/g, '$1'],
    [/([,.;:!?])([^\s])/g, '$1 $2'],
  ];
}

function correctText(value: string, lang: string): string {
  let next = value.trim().replace(/\s+/g, ' ');
  correctionPairs(lang).forEach(([pattern, replacement]) => {
    next = next.replace(pattern, replacement);
  });
  return next.replace(/(^|[.!?]\s+)([a-záéíóúñ])/g, (_, prefix: string, letter: string) => `${prefix}${letter.toUpperCase()}`);
}

function setStatus(text: string, active = false): void {
  if (!assistantEl) return;
  assistantEl.classList.toggle('is-listening', active);
  const status = assistantEl.querySelector<HTMLElement>('[data-profint-assistant-status]');
  if (status) status.textContent = text;
  syncAssistantFooter();
}

function syncAssistantFooter(): void {
  if (!assistantEl) return;
  const footer = assistantEl.closest<HTMLElement>('[data-intranet-footer]');
  if (!footer) return;
  const listening = assistantEl.classList.contains('is-listening');
  footer.dataset.assistantState = listening ? 'listening' : assistantEl.classList.contains('is-open') ? 'open' : 'idle';
  const label = assistantEl.querySelector<HTMLElement>('[data-assistant-label]');
  if (label) label.textContent = listening ? 'Escuchando' : 'Asistente';
}

function setDictationButtonState(button: HTMLButtonElement | null, listening: boolean): void {
  if (!button) return;
  button.classList.toggle('is-listening', listening);
  button.setAttribute('aria-pressed', listening ? 'true' : 'false');
  const icon = button.querySelector('i');
  const label = button.querySelector('span');
  if (icon) icon.className = listening ? 'ph ph-check-circle' : 'ph ph-microphone';
  if (label) label.textContent = listening ? 'Escuchando' : 'Dictar';
}

function previewText(target: HTMLTextAreaElement | HTMLInputElement, interim: string): void {
  const clean = interim.trim().replace(/\s+/g, ' ');
  target.value = clean ? `${committedTargetText}${committedTargetText ? ' ' : ''}${clean}` : committedTargetText;
  emitInput(target);
}

function stopRecognition(abort = false): void {
  keepListening = false;
  const current = recognition;
  recognition = null;
  setDictationButtonState(activeButton, false);
  activeButton = null;
  if (current) {
    if (abort) current.abort();
    else current.stop();
  }
  setStatus('Listo para escuchar', false);
}

function startRecognition(options: {
  target?: HTMLTextAreaElement | HTMLInputElement | null;
  onFinal?: (text: string) => void;
  button?: HTMLButtonElement | null;
}): void {
  const Ctor = recognitionCtor();
  if (!Ctor) {
    setStatus('El navegador no permite dictado por voz', false);
    return;
  }

  stopRecognition(true);
  activeTarget = options.target ?? null;
  activeButton = options.button ?? null;
  committedTargetText = activeTarget?.value.trimEnd() ?? '';
  keepListening = true;

  const next = new Ctor();
  next.lang = 'es-AR';
  next.continuous = true;
  next.interimResults = true;
  next.onresult = (event) => {
    let interim = '';
    for (let index = event.resultIndex; index < event.results.length; index += 1) {
      const result = event.results[index];
      const text = result[0].transcript.trim();
      if (!text) continue;
      if (result.isFinal) {
        if (activeTarget) {
          appendText(activeTarget, text);
          committedTargetText = activeTarget.value.trimEnd();
        }
        options.onFinal?.(text);
      } else {
        interim = `${interim} ${text}`.trim();
      }
    }
    if (activeTarget && interim) previewText(activeTarget, interim);
  };
  next.onerror = ({ error }) => {
    if (error === 'aborted' || error === 'no-speech') return;
    if (error === 'not-allowed' || error === 'service-not-allowed' || error === 'audio-capture') {
      keepListening = false;
    }
    setStatus(error === 'not-allowed' ? 'Habilitá el micrófono para dictar' : `No pudimos transcribir (${error})`, false);
  };
  next.onend = () => {
    recognition = null;
    if (!keepListening) {
      setStatus('Listo para escuchar', false);
      return;
    }
    window.setTimeout(() => startRecognition(options), 180);
  };

  try {
    recognition = next;
    next.start();
    setDictationButtonState(activeButton, true);
    setStatus(activeTarget ? 'Dictando en el campo seleccionado' : 'Asistente escuchando', true);
  } catch {
    keepListening = false;
    setDictationButtonState(activeButton, false);
    setStatus('No pudimos iniciar el micrófono', false);
  }
}

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function routeFor(text: string): string | null {
  const command = normalize(text);
  const aliases: Array<[RegExp, string]> = [
    [/(inicio|home|principal)/, '/'],
    [/(pacientes|padron)/, '/pacientes'],
    [/(turnos|agenda|calendario)/, '/turnos'],
    // A clinical history requires a patient; start with the existing patient picker.
    [/(historia clinica|historias clinicas|hc)/, '/pacientes'],
    [/(preocupacionales|laboral|medicina laboral)/, '/preocupacionales'],
    [/(licencias|turnos online de licencias)/, '/licencias-profesionales/turnos-online'],
    [/(informes|portal de informes)/, '/informes'],
    [/(tareas|pendientes)/, '/tareas'],
    [/(empresas|clientes)/, '/empresas'],
  ];
  return aliases.find(([pattern]) => pattern.test(command))?.[1] ?? null;
}

function handleAssistantCommand(text: string): void {
  const route = routeFor(text);
  if (!route) {
    setStatus('No entendí el destino. Probá: abrir pacientes, abrir turnos o abrir licencias', false);
    return;
  }
  setStatus('Abriendo pantalla', false);
  window.location.assign(route);
}

function mountDictationControl(target: HTMLTextAreaElement | HTMLInputElement): void {
  if (target.dataset.profintDictationMounted) return;
  target.dataset.profintDictationMounted = '1';

  const wrapper = document.createElement('div');
  wrapper.className = 'profint-dictation';
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'profint-dictation__button';
  button.setAttribute('aria-pressed', 'false');
  button.innerHTML = '<i class="ph ph-microphone" aria-hidden="true"></i><span>Dictar</span>';
  button.addEventListener('click', () => {
    if (keepListening && activeTarget === target) {
      stopRecognition();
      return;
    }
    startRecognition({ target, button });
  });
  const correctButton = document.createElement('button');
  correctButton.type = 'button';
  correctButton.className = 'profint-dictation__button profint-dictation__button--secondary';
  correctButton.innerHTML = '<i class="ph ph-check" aria-hidden="true"></i><span>Corregir texto</span>';
  const status = document.createElement('small');
  status.className = 'profint-dictation__status';
  correctButton.addEventListener('click', () => {
    const corrected = correctText(target.value, target.dataset.profintDictationLang || 'es-AR');
    if (!corrected || corrected === target.value) {
      status.textContent = 'Sin cambios sugeridos';
      return;
    }
    target.value = corrected;
    committedTargetText = corrected.trimEnd();
    emitInput(target);
    status.textContent = 'Texto corregido';
  });
  wrapper.appendChild(button);
  wrapper.appendChild(correctButton);
  wrapper.appendChild(status);
  target.insertAdjacentElement('beforebegin', wrapper);
}

function mountAssistant(): void {
  if (assistantEl || document.body.dataset.profintAssistant === 'off') return;

  const root = document.createElement('aside');
  root.className = 'profint-assistant';
  root.setAttribute('aria-label', 'Asistente Profint');
  root.innerHTML = `
    <button class="profint-assistant__toggle" type="button" aria-label="Abrir asistente de voz" aria-expanded="false" aria-controls="profint-assistant-panel">
      <i class="ph ph-sparkle" aria-hidden="true"></i>
      <span data-assistant-label>Asistente</span>
    </button>
    <div class="profint-assistant__panel" id="profint-assistant-panel" hidden>
      <div class="profint-assistant__head">
        <strong>Asistente Profint</strong>
        <button type="button" class="profint-assistant__close" aria-label="Cerrar asistente"><i class="ph ph-x" aria-hidden="true"></i></button>
      </div>
      <p data-profint-assistant-status>Listo para escuchar</p>
      <div class="profint-assistant__signal" aria-hidden="true">${bars}</div>
      <button class="profint-assistant__listen" type="button">
        <i class="ph ph-microphone" aria-hidden="true"></i>
        <span>Escuchar comando</span>
      </button>
    </div>`;

  const toggle = root.querySelector<HTMLButtonElement>('.profint-assistant__toggle');
  const close = root.querySelector<HTMLButtonElement>('.profint-assistant__head button');
  const panel = root.querySelector<HTMLElement>('.profint-assistant__panel');
  const listen = root.querySelector<HTMLButtonElement>('.profint-assistant__listen');
  const setPanelOpen = (open: boolean): void => {
    if (!panel) return;
    panel.hidden = !open;
    root.classList.toggle('is-open', open);
    toggle?.setAttribute('aria-expanded', String(open));
    toggle?.setAttribute('aria-label', open ? 'Cerrar asistente de voz' : 'Abrir asistente de voz');
    if (!open) stopRecognition();
    syncAssistantFooter();
  };
  toggle?.addEventListener('click', () => {
    if (!panel) return;
    setPanelOpen(panel.hidden);
  });
  close?.addEventListener('click', () => {
    setPanelOpen(false);
    stopRecognition();
    toggle?.focus();
  });
  root.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !panel || panel.hidden) return;
    event.preventDefault();
    setPanelOpen(false);
    stopRecognition();
    toggle?.focus();
  });
  listen?.addEventListener('click', () => {
    if (keepListening && !activeTarget) {
      stopRecognition();
      return;
    }
    startRecognition({ onFinal: handleAssistantCommand });
  });

  assistantEl = root;
  const dock = document.querySelector<HTMLElement>('[data-assistant-dock]');
  if (dock) {
    root.classList.add('profint-assistant--docked');
    dock.appendChild(root);
    syncAssistantFooter();
  } else {
    document.body.appendChild(root);
  }
}

function enhance(scope: ParentNode = document): void {
  scope
    .querySelectorAll<HTMLTextAreaElement | HTMLInputElement>('textarea[data-profint-dictation], input[data-profint-dictation]')
    .forEach(mountDictationControl);
}

document.addEventListener('DOMContentLoaded', () => {
  enhance();
  mountAssistant();
});

document.body?.addEventListener('htmx:afterSwap', (event) => {
  const target = (event as CustomEvent).detail?.target as ParentNode | undefined;
  enhance(target ?? document);
});
